import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LookupProvider } from './contexts/LookupContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import ContactCardPage from './pages/ContactCardPage';
import DashboardPage from './pages/DashboardPage';
import ContactsPage from './pages/ContactsPage';
import ContactDetailPage from './pages/ContactDetailPage';
import PipelinePage from './pages/PipelinePage';
import GroupsPage from './pages/GroupsPage';
import ReportsPage from './pages/ReportsPage';
import TemplatesPage from './pages/TemplatesPage';
import CalendarPage from './pages/CalendarPage';
import TaskListPage from './pages/TaskListPage';
import ImportExportPage from './pages/ImportExportPage';
import SettingsPage from './pages/SettingsPage';
import PrintMergePage from './pages/PrintMergePage';
import SearchPage from './pages/SearchPage';
import HelpPage from './pages/HelpPage';
import TutorialsPage from './pages/TutorialsPage';
import MarketingKitPage from './pages/MarketingKitPage';
import SmartImportPage from './pages/SmartImportPage';
import MigrationCrunchPage from './pages/MigrationCrunchPage';
import MigrationPairPage from './pages/MigrationPairPage';
import MigrationComparePage from './pages/MigrationComparePage';
import WalkthroughPage from './pages/WalkthroughPage';
import ConciergeExperiencePage from './pages/ConciergeExperiencePage';
import NoteCrunchLegalPage from './pages/NoteCrunchLegalPage';
import SalesCrunchWalkthroughPage from './pages/SalesCrunchWalkthroughPage';
import CompetitorComparePage from './pages/CompetitorComparePage';
import AffiliatesPage from './pages/AffiliatesPage';
import LogosPage from './pages/LogosPage';
import AIAgentsPage from './pages/AIAgentsPage';
import ManifestoPage from './pages/ManifestoPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SheetSyncPage from './pages/SheetSyncPage';
import HomePage from './pages/HomePage';
import SalesCrunchGeminiPage from './pages/SalesCrunchGeminiPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import TopupSuccessPage from './pages/TopupSuccessPage';
import MagicLoginPage from './pages/MagicLoginPage';
import AuditPage from './pages/AuditPage';
import MergeDuplicatesPage from './pages/MergeDuplicatesPage';
import AffiliateAgreementPage from './pages/AffiliateAgreementPage';
import ContactPage from './pages/ContactPage';
import RecoverLicensePage from './pages/RecoverLicensePage';
import GeminiReviewPage from './pages/GeminiReviewPage';
import SalesforceTrueCostPage from './pages/SalesforceTrueCostPage';
import MarketingGeneratorPage from './pages/MarketingGeneratorPage';
import VoiceCorrectorPage from './pages/VoiceCorrectorPage';
import VideoCrunchPage from './pages/VideoCrunchPage';
import VideoCrunchAppPage from './pages/VideoCrunchAppPage';
import VideoCrunchJobsAdminPage from './pages/VideoCrunchJobsAdminPage';
import VoiceRulebookPage from './pages/VoiceRulebookPage';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center" data-testid="auth-loading">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  // Reddit Pixel — SPA route tracking. The `rdt('track','PageVisit')` in
  // /public/index.html only fires once on initial page load; without this
  // effect, Reddit sees /migrate but never /migrate/gemini, /migrate/compare,
  // /migrate/recover-license, /vs-migratemycrm, etc. That kills conversion
  // optimization on the paid Reddit campaign because the algorithm can't
  // learn which pages correlate with `Lead` events.
  //
  // We skip the very first pathname because index.html already fired for it.
  const initialPixelPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === initialPixelPathRef.current) {
      initialPixelPathRef.current = null; // arm for future changes
      return;
    }
    if (typeof window !== 'undefined' && typeof window.rdt === 'function') {
      try {
        window.rdt('track', 'PageVisit');
      } catch (_) {
        /* Reddit pixel failed to fire — no-op, don't break the page. */
      }
    }
  }, [location.pathname]);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {/* /pricing — convenience URL that lands on HomePage and scrolls to the
          pricing section. Used in the video brief and any "see our pricing"
          marketing link. */}
      <Route path="/pricing" element={<HomePage scrollTo="pricing" />} />
      {/* /gemini — SalesCrunch-side "What Gemini said" page (sibling of
          /migrate/gemini): Gemini's review of the ACT! 4.0 UX strategy. */}
      <Route path="/gemini" element={<SalesCrunchGeminiPage />} />
      <Route path="/billing/success" element={<CheckoutSuccessPage />} />
      <Route path="/billing/topup-success" element={<TopupSuccessPage />} />
      <Route path="/auth/magic" element={<MagicLoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* /signup and /register — convenience URLs that land on the login
          page with the Register tab pre-selected. Used in marketing links,
          email signup CTAs, and so people who type /signup don't 404. */}
      <Route path="/signup" element={<LoginPage defaultMode="register" />} />
      <Route path="/register" element={<LoginPage defaultMode="register" />} />
      {/* /tools/marketing-generator — PUBLIC demo of Crunchy's writing chops.
          Rate-limited (3 per IP per day) lead-gen surface. */}
      <Route path="/tools/marketing-generator" element={<MarketingGeneratorPage />} />
      <Route path="/migrationcrunch" element={<MigrationCrunchPage />} />
      {/* /migrate — friendly short alias for the MigrationCrunch landing page;
          marketing/SEO links and migrationcrunch.com DNS both resolve here. */}
      <Route path="/migrate" element={<MigrationCrunchPage />} />
      {/* /migrate/compare — competitive comparison landing page targeting
          "best CRM migration tool" / "MigrateMyCRM vs Trujay" SEO queries. */}
      <Route path="/migrate/compare" element={<MigrationComparePage />} />
      {/* /migrate/walkthrough — visual step-by-step tour of the entire
          MigrationCrunch flow. Linked from nav, hero CTA, and footer. */}
      <Route path="/migrate/walkthrough" element={<WalkthroughPage />} />
      {/* /migrate/concierge — "what exactly happens" page for the done-for-
          you Concierge add-on. Linked from pricing, nav and intake modal. */}
      <Route path="/migrate/concierge" element={<ConciergeExperiencePage />} />
      {/* /notecrunch — NoteCrunch Legal: standalone pitch demo for legal-
          migration platforms (Universal Migrator licensing play). */}
      <Route path="/notecrunch" element={<NoteCrunchLegalPage />} />
      {/* /walkthrough — SalesCrunchCRM 10-step visual tour. Linked from
          the homepage hero CTA and the top nav. Parallels /migrate/walkthrough. */}
      <Route path="/walkthrough" element={<SalesCrunchWalkthroughPage />} />
      <Route path="/tutorials" element={<TutorialsPage />} />
      <Route path="/voice" element={<VoiceCorrectorPage />} />
      <Route path="/videocrunch" element={<VideoCrunchPage />} />
      <Route path="/videocrunch/app" element={<VideoCrunchAppPage />} />
      {/* /migrate/vs/:competitor — dedicated head-to-head SEO landing pages
          (e.g. /migrate/vs/migratemycrm, /vs/import2, /vs/trujay, /vs/slalom).
          See data/competitors.js for the supported slugs. Unknown slugs
          redirect back to /migrate/compare. */}
      <Route path="/migrate/vs/:competitor" element={<CompetitorComparePage />} />
      {/* Short-URL aliases for keyword-ad landing pages — Chip is running
          Google Ads on competitor keywords and wants clean, short URLs
          (/vs-import2 vs /migrate/vs/import2) for lower cost-per-click and
          better display. Both render the same CompetitorComparePage; the
          alias injects the competitor via the `slugOverride` prop. */}
      <Route path="/vs-import2" element={<CompetitorComparePage slugOverride="import2" />} />
      <Route path="/vs-migratemycrm" element={<CompetitorComparePage slugOverride="migratemycrm" />} />
      <Route path="/vs-trujay" element={<CompetitorComparePage slugOverride="trujay" />} />
      {/* /privacy — public Privacy Policy page. Linked from MigrationCrunch
          footer + cookie banner. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      {/* /terms — public Terms of Service. Linked from footer. */}
      <Route path="/terms" element={<TermsOfServicePage />} />
      {/* /migrate/:pair — pair-specific SEO landing pages
          (e.g. /migrate/act-to-hubspot). See data/migration_pairs.js. */}
      <Route path="/migrate/recover-license" element={<RecoverLicensePage />} />
      <Route path="/migrate/gemini" element={<GeminiReviewPage />} />
      {/* /migrate/salesforce-true-cost — CFO-math SEO page: 3-year Salesforce
          TCO (licenses + setup + admin) vs SalesCrunchCRM $30/mo flat. */}
      <Route path="/migrate/salesforce-true-cost" element={<SalesforceTrueCostPage />} />
      <Route path="/migrate/:pair" element={<MigrationPairPage />} />
      <Route path="/affiliate-agreement" element={<AffiliateAgreementPage />} />
      {/* /affiliates — public affiliate-program landing page with downloadable
          media kit (logos + banners + email swipes). Funnels visitors to
          Rewardful signup. The legal /affiliate-agreement page above is
          linked from here for full T&Cs. */}
      <Route path="/affiliates" element={<AffiliatesPage />} />
      {/* /logos — one-click downloadable logo library for all three products */}
      <Route path="/logos" element={<LogosPage />} />
      {/* /ai-agents — built-in agents vs DIY agent builders (Twin.so et al) */}
      <Route path="/ai-agents" element={<AIAgentsPage />} />
      <Route path="/manifesto" element={<ManifestoPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/anti-agent" element={<Navigate to="/ai-agents" replace />} />
      {/* /affiliate — singular alias so people who guess that URL don't 404. */}
      <Route path="/affiliate" element={<Navigate to="/affiliates" replace />} />
      <Route path="/partners" element={<Navigate to="/affiliates" replace />} />
      <Route path="/contact-us" element={<ContactPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/contact" element={<ContactCardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/tasks" element={<TaskListPage />} />
        <Route path="/print" element={<PrintMergePage />} />
        {/* /print-merge — legacy alias mentioned in the video brief and older
            marketing pages. Redirects to the canonical /print route. */}
        <Route path="/print-merge" element={<Navigate to="/print" replace />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/kit" element={<MarketingKitPage />} />
        <Route path="/smart-import" element={<SmartImportPage />} />
        <Route path="/sheet-sync" element={<SheetSyncPage />} />
        <Route path="/import-export" element={<ImportExportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin/audit" element={<AuditPage />} />
        {/* /audit — friendly alias used in QA scripts; redirect to canonical path */}
        <Route path="/audit" element={<Navigate to="/admin/audit" replace />} />
        <Route path="/admin/merge" element={<MergeDuplicatesPage />} />
        <Route path="/admin/voice-rules" element={<VoiceRulebookPage />} />
        <Route path="/admin/videocrunch-jobs" element={<VideoCrunchJobsAdminPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LookupProvider>
          <AppRouter />
          <Toaster richColors closeButton />
        </LookupProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
